/**
 * ═══════════════════════════════════════════════════════════════════════════
 * scheduler-shared.types.ts - Tipos Compartidos para Scheduler
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @version 1.0.0
 * @description Tipos compartidos entre componentes del Scheduler para evitar
 *              incompatibilidades de tipos
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

// ═══════════════════════════════════════════════════════════════════════════
// CONTACT TYPES - COMPATIBLES CON mockContactsDatabase
// ═══════════════════════════════════════════════════════════════════════════

export interface ContactDB {
  id: string;
  name: string;
  phone: string;
  email?: string;
  company?: string;
  position?: string;
  tags: string[];
  groups: string[];
  status: 'active' | 'inactive' | 'bounced' | 'unsubscribed';
  source?: 'manual' | 'import' | 'api' | 'webform' | 'oneshot';
  created_at?: string;
  updated_at?: string;
  location?: {
    lat: number;
    lng: number;
    address?: string;
    source?: string;
  };
}

export interface ContactGroup {
  id: string;
  name: string;
  description?: string;
  // count es opcional porque el tipo real no lo tiene
  count?: number;
}

export interface ContactTag {
  id: string;
  name: string;
  color?: string;
  // count es opcional porque el tipo real no lo tiene
  count?: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// RISK TYPES
// ═══════════════════════════════════════════════════════════════════════════

export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

export interface RiskData {
  score: number;
  level: RiskLevel;
  flagProbability: number;
  mainIssues?: string[];
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPER TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Convierte array de IDs a Set
 */
export const arrayToSet = (arr: string[]): Set<string> => new Set(arr);

/**
 * Convierte Set de IDs a array
 */
export const setToArray = (set: Set<string>): string[] => Array.from(set);

/**
 * Agrega count a grupos si no lo tienen
 */
export const enrichGroups = (groups: ContactGroup[], contacts: ContactDB[]): ContactGroup[] => {
  return groups.map(group => ({
    ...group,
    count: group.count ?? contacts.filter(c => c.groups.includes(group.id)).length
  }));
};

/**
 * Agrega count a tags si no lo tienen
 */
export const enrichTags = (tags: ContactTag[], contacts: ContactDB[]): ContactTag[] => {
  return tags.map(tag => ({
    ...tag,
    count: tag.count ?? contacts.filter(c => c.tags.includes(tag.id)).length
  }));
};